import type { ApolloError } from "@apollo/client"
import { Alert, AlertTitle, Box, Button } from "@mui/material"
import { useRouter } from "next/router"

interface ErrorDisplayProps {
  error: ApolloError | Error
  onRetry?: () => void
}

/**
 * GraphQL エラーや一般的なエラーを表示するコンポーネント
 */
export function ErrorDisplay({ error, onRetry }: ErrorDisplayProps) {
  const router = useRouter()

  // Apollo エラーから詳細情報を抽出
  const getErrorInfo = () => {
    if ("graphQLErrors" in error && error.graphQLErrors.length > 0) {
      const graphQLError = error.graphQLErrors[0]
      const code = graphQLError.extensions?.code as string | undefined
      const userMessage = graphQLError.extensions?.userMessage as string | undefined

      return {
        code,
        message: userMessage || graphQLError.message,
      }
    }

    return {
      code: undefined,
      message: error.message,
    }
  }

  const { code, message } = getErrorInfo()

  // エラーコードに応じた適切なメッセージとアクションを決定
  const getErrorDisplay = () => {
    switch (code) {
      case "UNAUTHORIZED":
        return {
          severity: "warning" as const,
          title: "Authentication Error",
          message: "Your session has expired. Please login again.",
          action: (
            <Button
              color="inherit"
              size="small"
              onClick={() => router.push("/")}
            >
              Go to Login
            </Button>
          ),
        }

      case "DATABASE_SCHEMA_ERROR":
        return {
          severity: "error" as const,
          title: "Database Error",
          message:
            "Database schema is outdated. This may be a development environment. Please use the production environment.",
          action: onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined,
        }

      case "DATABASE_ERROR":
        return {
          severity: "error" as const,
          title: "Database Error",
          message: "A database error occurred. Please try again later.",
          action: onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined,
        }

      case "NOT_FOUND":
        return {
          severity: "info" as const,
          title: "Not Found",
          message: "The requested data was not found.",
          action: (
            <Button color="inherit" size="small" onClick={() => router.push("/")}>
              Go to Home
            </Button>
          ),
        }

      case "FORBIDDEN":
        return {
          severity: "warning" as const,
          title: "Access Denied",
          message: "You do not have permission to access this resource.",
          action: (
            <Button color="inherit" size="small" onClick={() => router.push("/")}>
              Go to Home
            </Button>
          ),
        }

      default:
        return {
          severity: "error" as const,
          title: "Error Occurred",
          message: message || "An unexpected error occurred.",
          action: onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined,
        }
    }
  }

  const errorDisplay = getErrorDisplay()

  return (
    <Box sx={{ p: 2 }}>
      <Alert severity={errorDisplay.severity} action={errorDisplay.action}>
        <AlertTitle>{errorDisplay.title}</AlertTitle>
        {errorDisplay.message}
      </Alert>
    </Box>
  )
}
